import { wccModules } from 'enums/wccModules';
import { DOMHelpers } from 'helpers/dom';
import { inject, injectable } from 'inversify';
import { Observable, ObservableArray, Subscribable, SubscribableOrNullableValue } from 'knockout';
import 'libraries/select2';
import { withEffect } from 'mixins/withEffect';
import { EffectsContainer } from 'mixins/withEffects';
import { DataFormat, GroupedDataFormat } from 'select2';
import './select2.html';
import './select2.scss';

export interface Select2VMConfig {
    multiple?: SubscribableOrNullableValue<boolean>,
    data: SubscribableOrNullableValue<DataFormat[]> | SubscribableOrNullableValue<GroupedDataFormat[]>
}

@injectable()
export default class Select2VM {
    selectNode: Observable<HTMLSelectElement | undefined> = ko.observable()
    selection: ObservableArray<string> = ko.observableArray()

    constructor(
        @inject(wccModules.componentConfig) config: Select2VMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const data = ko.flattenComputed(config.data as SubscribableOrNullableValue<DataFormat[] | GroupedDataFormat[]>, []);
        const isMultiSelect = ko.flattenComputed(config.multiple, false);

        effects.register((node, isMultiSelect) => {
            if (node != undefined)
                return this.initSelect2(node, data, isMultiSelect);
        }, [this.selectNode, isMultiSelect]);

        ko.applyBindingsToNode(info.element, {
            'css': {
                'wcc-select-2': true
            }
        }, this);
    }

    private initSelect2(select: HTMLSelectElement, items: Subscribable<DataFormat[] | GroupedDataFormat[]>, isMultiSelect: boolean) {
        const $select = $(select);

        $select.select2({
            multiple: isMultiSelect,
            data: items()
        });

        return [
            withEffect(items => this.updateItems($select, items), [items]),

            withEffect(selection => this.updateSelectionFromSource($select, selection), [this.selection]),
            DOMHelpers.onNodeEvent($select, 'change', () => this.updateSelectionFromSelect($select)),

            () => $select.select2('destroy')
        ]
    }

    private updateItems($select: JQuery<HTMLSelectElement>, items: DataFormat[] | GroupedDataFormat[]) {
        $select.empty();

        items.forEach((itemOrGroupData: DataFormat | GroupedDataFormat) => {
            if ('children' in itemOrGroupData) {
                const groupData = itemOrGroupData as GroupedDataFormat;
                const group = document.createElement('optgroup');
                group.label = groupData.text;

                groupData.children?.forEach(itemData => {
                    group.appendChild(new Option(itemData.text, itemData.id.toString(), false, false));
                });

                $select.append(group);
            } else {
                const itemData = itemOrGroupData as DataFormat;
                $select.append(new Option(itemData.text, itemData.id.toString(), false, false))
            }
        });

        $select.trigger('change');
    }

    private updateSelectionFromSource($select: JQuery<HTMLSelectElement>, selection: Array<string>) {
        const currentSelection = $select.select2('data').map(item => item.id);

        if (!system.isTheSameList(currentSelection, selection)) {
            $select.val(selection).trigger('change');
        }
    }

    private updateSelectionFromSelect($select: JQuery<HTMLSelectElement>) {
        this.selection($select.select2('data').map(item => item.id));
    }
}